/**
 * useTopCauses - Data fetching hook for top root causes
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterConfig } from '../../../records/filter';
import type {
  CauseReportTopCauses,
  TopRootCause,
  UseTopCausesReturn
} from '../types';
import {
  topCausesKeys,
  CAUSE_REPORT_STALE_TIME,
  TOP_CAUSES_LIMIT,
} from '../constants';
import { fetchWithAuth } from '../../../../../utils/api';
import { mapFiltersToApiParams } from '../utils';

interface TopCausesApiResponse {
  causes: Array<{
    rank: number;
    label_id: string;
    label_name: string;
    category_id: string;
    category_name: string;
    count: number;
    delta: number;
    delta_previous: number;
  }>;
  total_records: number;
  category_filter: string | null;
}

function transformTopCauses(data: TopCausesApiResponse): CauseReportTopCauses {
  return {
    causes: data.causes.map((cause): TopRootCause => ({
      rank: cause.rank,
      labelId: cause.label_id,
      labelName: cause.label_name,
      categoryId: cause.category_id,
      categoryName: cause.category_name,
      count: cause.count,
      delta: cause.delta,
      deltaPrevious: cause.delta_previous,
    })),
    totalRecords: data.total_records,
    categoryFilter: data.category_filter || undefined,
  };
}

async function fetchTopCauses(
  datasetId: string,
  params: Record<string, string>,
  categoryFilter?: string
): Promise<TopCausesApiResponse> {
  const queryParams = { ...params };

  if (categoryFilter) {
    queryParams.category_filter = categoryFilter;
  }
  queryParams.limit = String(TOP_CAUSES_LIMIT);

  const queryString = new URLSearchParams(queryParams).toString();

  return fetchWithAuth(
    `/v1/datasets/${datasetId}/cause-report/top-causes?${queryString}`
  );
}

export function useTopCauses(
  datasetId: string,
  filters: FilterConfig,
  categoryFilter?: string
): UseTopCausesReturn {
  const filterParams = useMemo(() => mapFiltersToApiParams(filters), [filters]);

  const query = useQuery({
    queryKey: topCausesKeys.list(datasetId, filterParams, categoryFilter),
    queryFn: () => fetchTopCauses(datasetId, filterParams, categoryFilter),
    select: transformTopCauses,
    staleTime: CAUSE_REPORT_STALE_TIME,
    enabled: !!datasetId,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
